import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api, errMsg, fmtDate } from "@/lib/api";
import { inputClass } from "@/components/FieldInput";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";

const fill = (body, params) => body.replace(/\{\{(\d+)\}\}/g, (m, n) => params[n - 1] || m);

export function WhatsAppSendDialog({ trust, person, onClose }) {
  const [templates, setTemplates] = useState(null);
  const [deadlines, setDeadlines] = useState([]);
  const [tpl, setTpl] = useState("");
  const [deadlineId, setDeadlineId] = useState("");
  const [params, setParams] = useState([]);
  const [mode, setMode] = useState("template");
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/whatsapp/templates").then((r) => setTemplates(r.data)).catch((e) => { toast.error(errMsg(e)); setTemplates([]); });
    api.get("/e/deadlines", { params: { trust_id: trust.id } }).then((r) => setDeadlines(r.data.filter((d) => d.status === "pendente"))).catch(() => {});
  }, [trust.id]);

  const t = useMemo(() => (templates || []).find((x) => `${x.name}|${x.language}` === tpl), [templates, tpl]);
  useEffect(() => {
    if (!t) return;
    const d = deadlines.find((x) => x.id === deadlineId);
    const auto = [person.first_name || person.name, trust.name, d?.title || "", d ? fmtDate(d.due_date) : ""];
    setParams(Array.from({ length: t.variables }, (_, i) => auto[i] || ""));
  }, [t, deadlineId, deadlines, person, trust.name]);

  const send = async () => {
    setBusy(true);
    try {
      const body = mode === "text" ? { text } : { template_name: t.name, language: t.language, params };
      await api.post("/whatsapp/send", { trust_id: trust.id, subject_id: person.subject_id, deadline_id: deadlineId || null, ...body });
      toast.success("Messaggio WhatsApp inviato");
      onClose(true);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const ready = mode === "text" ? text.trim() : t && params.every((p) => p.trim());

  return (
    <Dialog open onOpenChange={(o) => !o && onClose(false)}>
      <DialogContent className="max-w-xl bg-white" data-testid="wa-send-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">WhatsApp a {person.name}</DialogTitle>
          <DialogDescription>+{person.wa_id} · {person.window_open ? "il cliente ha scritto nelle ultime 24 ore" : "solo modelli approvati (finestra 24 ore chiusa)"}</DialogDescription>
        </DialogHeader>
        {person.window_open && (
          <div className="flex gap-2 text-xs">
            {[["template", "Modello approvato"], ["text", "Testo libero"]].map(([v, l]) => (
              <button key={v} onClick={() => setMode(v)} data-testid={`wa-mode-${v}`}
                className={`px-3 py-1.5 rounded-full border ${mode === v ? "bg-[#0A2540] text-white border-[#0A2540]" : "border-slate-300 text-slate-600"}`}>{l}</button>
            ))}
          </div>
        )}
        {mode === "text" ? (
          <textarea className={`${inputClass} h-28 py-2`} maxLength={4096} value={text} onChange={(e) => setText(e.target.value)} data-testid="wa-free-text" />
        ) : (
          <div className="space-y-3">
            <select className={inputClass} value={tpl} onChange={(e) => setTpl(e.target.value)} data-testid="wa-template-select">
              <option value="">{templates === null ? "Caricamento modelli…" : templates.length ? "— Scegli il modello —" : "Nessun modello approvato"}</option>
              {(templates || []).map((x) => <option key={`${x.name}|${x.language}`} value={`${x.name}|${x.language}`}>{x.name} ({x.language})</option>)}
            </select>
            <select className={inputClass} value={deadlineId} onChange={(e) => setDeadlineId(e.target.value)} data-testid="wa-deadline-select">
              <option value="">— Scadenza da richiamare (facoltativa) —</option>
              {deadlines.map((d) => <option key={d.id} value={d.id}>{fmtDate(d.due_date)} · {d.title}</option>)}
            </select>
            {t && params.map((p, i) => (
              <div key={i}>
                <label className="text-[11px] text-slate-500">{`Variabile {{${i + 1}}}`}</label>
                <input className={inputClass} value={p} onChange={(e) => setParams((s) => s.map((x, j) => (j === i ? e.target.value : x)))} data-testid={`wa-param-${i + 1}`} />
              </div>
            ))}
            {t && <p className="text-sm bg-emerald-50 border border-emerald-100 rounded-lg p-3 whitespace-pre-wrap" data-testid="wa-preview">{fill(t.body, params)}</p>}
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => onClose(false)} data-testid="wa-cancel">Annulla</Button>
          <Button disabled={busy || !ready} onClick={send} className="bg-[#0A2540] hover:bg-[#031B33] text-white" data-testid="wa-send-submit">{busy ? "Invio…" : "Invia"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
